import React, { useCallback, useContext, useState } from "react";

import {
  Flex,
  NoSSR,
  NotificationBadge,
  Text,
} from "@catchoftheday/cg-components";

import { MOBILE_BOTTOM_NAV_HEIGHT } from "@header/consts";

import { AccountMenu } from "./AccountMenu";
import { CategoryMenu } from "./CategoryMenu";
import { BOTTOM_NAV, BottomNavItem } from "./consts";
import { MobileMenuContext } from "./MobileMenuContext";
import { MobileNavItem } from "./MobileNavItem";

const CATEGORY_MENU = "category_menu";
const ACCOUNT_MENU = "account_menu";

export const MobileNav = () => {
  const { wishlistItemsCount = 0, cartItemsCount = 0 } =
    useContext(MobileMenuContext);

  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  const toggleMenuVisibility = useCallback((menuId?: string) => {
    if (!menuId) return;

    if (menuId === CATEGORY_MENU) {
      setShowCategoryMenu((prevState) => !prevState);
      setShowAccountMenu(false);
    } else if (menuId === ACCOUNT_MENU) {
      setShowCategoryMenu(false);
      setShowAccountMenu((prevState) => !prevState);
    }
  }, []);

  const isActive = useCallback(
    (navItem: BottomNavItem) => {
      const isOnActivePage =
        typeof window !== "undefined" &&
        window.location.pathname !== "#" &&
        window.location.pathname === navItem.url;

      const isCategoryOrAccountMenu =
        (navItem.title === "Categories" && showCategoryMenu) ||
        (navItem.title === "Account" && showAccountMenu);

      return (
        isCategoryOrAccountMenu ||
        (!showCategoryMenu && !showAccountMenu && isOnActivePage)
      );
    },
    [showCategoryMenu, showAccountMenu]
  );

  return (
    <>
      <Flex
        aria-label="Mobile navigation"
        as="nav"
        flexDirection="row"
        position="fixed"
        bottom="0px"
        bg="ui.backgroundColor"
        width="100%"
        height={`calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`}
        minHeight={MOBILE_BOTTOM_NAV_HEIGHT}
        pb="env(safe-area-inset-bottom)"
        zIndex={51}
        alignItems="center"
        justifyContent="center"
        boxShadow="0px -1px 0px #DDDDDD"
      >
        {BOTTOM_NAV.map((navItem: BottomNavItem, index) => {
          return (
            <MobileNavItem
              aria-label={`Mobile ${navItem.title}`}
              key={`${navItem.title}-${index}`}
              url={navItem.url}
              isActive={isActive(navItem)}
              onClick={() =>
                toggleMenuVisibility(
                  navItem.title === "Categories"
                    ? CATEGORY_MENU
                    : navItem.title === "Account"
                    ? ACCOUNT_MENU
                    : undefined
                )
              }
            >
              {navItem.title === "Cart" && cartItemsCount > 0 && (
                <NoSSR>
                  <NotificationBadge
                    aria-label="Cart item count"
                    text={cartItemsCount}
                    top="2px"
                    left="calc(50% + 7px)"
                    absRight="auto"
                    lineHeight="14px"
                    minWidth="auto"
                    fontSize="2xs"
                    padding="0 2px"
                  />
                </NoSSR>
              )}
              {navItem.title === "Wishlist" && wishlistItemsCount > 0 && (
                <NoSSR>
                  <NotificationBadge
                    aria-label="Wishlist item count"
                    text=""
                    top="4px"
                    left="calc(50% + 4px)"
                    height="10px"
                    width="10px"
                    minWidth="auto"
                  />
                </NoSSR>
              )}
              {isActive(navItem) ? (
                <navItem.activeIcon
                  width="20px"
                  height="20px"
                  title={navItem.title}
                />
              ) : (
                <navItem.icon width="20px" height="20px" />
              )}
              <Text m="0px" fontWeight="medium" fontSize="xs">
                {navItem.title}
              </Text>
            </MobileNavItem>
          );
        })}
      </Flex>
      <CategoryMenu
        isOpen={showCategoryMenu}
        toggleMenu={() => toggleMenuVisibility(CATEGORY_MENU)}
      />
      <AccountMenu isOpen={showAccountMenu} />
    </>
  );
};
